import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import * as s from './styled-users';
import { icons } from '../../assets';
import {
  EmptyState,
  Icon,
  ModalClose,
  ModalConfirmation,
} from '../../components';
import { notifyError, notifySuccess, useDebounce } from '../../utils';
import {
  getUsers,
  postNewUser,
  postEditUser,
  postEditStatusUser,
  deleteExistentUser,
  setUserPassword,
  getListaDeEmpresasDoFiltroDaTelaRelacaoUsuarios,
} from '../../services';

// Lazy-load do modal de cadastro/edicao — bundle pesado (Formik + yup
// + selects de empresa). So carrega quando o operador abre.
const CadastroUsuarioModal = lazy(() =>
  import('../../components/user-form/user-form')
);

// Inline icons (lucide-style) — chrome da tela. Acoes da linha
// (pencil/trash) continuam usando <Icon> porque ja resolve.
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const IPlus = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

// Roles disponiveis no select da linha. Vem do design + da regra de
// negocio existente: company tem acesso a "admin", customer apenas
// a "operador"/"visualizador". listPapel e filtrado por contexto.
const ROLE_LABEL = {
  admin: 'Admin Local',
  operador: 'Operador',
  visualizador: 'Visualizador',
  higra: 'Higra',
};

const formatLastAccess = (lastAccess) => {
  if (!lastAccess) return 'Sem registro';

  const date = new Date(lastAccess);
  if (Number.isNaN(date.getTime())) return 'Sem registro';

  return format(date, 'dd/MM/yyyy HH:mm');
};

const RelacaoUsuarios = () => {
  // ---- estados de dados ----
  const [currentData, setCurrentData] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listaDeEmpresasRelacaoUsuario, setListaDeEmpresasRelacaoUsuario] =
    useState([]);

  // ---- filtros ----
  const [filterText, setFilterText] = useState('');
  const debouncedFilterText = useDebounce(filterText, 350);
  const [currentEmpresa, setCurrentEmpresa] = useState(''); // value formato "id-type"
  const [currentRole, setCurrentRole] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(10);

  // ---- modais ----
  const [closeConfirmation, setCloseConfirmation] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [telaConcluido, setTelaConcluido] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);

  const [isModalEditVisible, setIsModalEditVisible] = useState(false);
  const [isModalEditVisibleConcluido, setIsModalEditVisibleConcluido] =
    useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [usuarioEditado, setUsuarioEditado] = useState();
  const [usuarioSelecionado, setUsuarioSelecionado] = useState({
    name: '',
    email: '',
    empresa: '',
    papel: '',
  });

  // Confirmacao de delete — armazena o usuario sendo excluido. null = modal fechado.
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState('');

  const { user } = useSelector((state) => state.usuario);

  // listPapel restrito ao contexto do usuario logado.
  // - company (usuarios Higra/empresa) podem criar Admin Local + outros.
  // - customer (clientes finais) so podem criar Operador/Visualizador.
  const listPapel = useMemo(
    () =>
      user.company
        ? [
            { label: 'Admin Local', value: 'admin' },
            { label: 'Operador', value: 'operador' },
            { label: 'Visualizador', value: 'visualizador' },
          ]
        : [
            { label: 'Operador', value: 'operador' },
            { label: 'Visualizador', value: 'visualizador' },
          ],
    [user.company]
  );

  // Empresa default no formulario de cadastro — usuario customer
  // (cliente final) ja vem com seu proprio customer fixo.
  const formikInitialValues = {
    name: '',
    email: '',
    empresa: user.customer ? `${user.customer.id}-customer` : '',
    company: false,
    papel: '',
  };

  // ============================================================
  // Fetch — empresas (filtros) + usuarios (lista paginada).
  // ============================================================
  const buscaListaEmpresasFiltro = useCallback(() => {
    getListaDeEmpresasDoFiltroDaTelaRelacaoUsuarios()
      .then(({ data }) => setListaDeEmpresasRelacaoUsuario(data))
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar as empresas');
      });
  }, []);

  // Token de fetch — incrementado a cada nova busca. Quando uma response
  // chega, comparamos com o token atual: se for diferente, descarta.
  // Evita race quando o operador troca filtros rapido (response antiga
  // chega depois da nova e sobrescreve dados corretos).
  const fetchTokenRef = useRef(0);

  const buscaUsuarios = useCallback(() => {
    let isCustomer = true;
    let currentEmpresaIdSemType;
    if (currentEmpresa) {
      // value e "${id}-${type}" pra evitar colisao entre customer/company
      // com mesmo id. Aqui desfazemos pra mandar pro backend.
      const [empresaId, empresaType] = currentEmpresa.split('-');
      currentEmpresaIdSemType = empresaId;
      isCustomer = empresaType === 'customer';
    }

    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setLoading(true);
    getUsers(
      currentPage + 1,
      itensPerPage,
      debouncedFilterText,
      currentRole,
      currentStatus,
      currentEmpresaIdSemType,
      isCustomer
    )
      .then(({ data }) => {
        if (myToken !== fetchTokenRef.current) return;
        const [realData, newAmount] = data;
        setCurrentData(realData || []);
        setAmount(newAmount || 0);
      })
      .catch((error) => {
        if (myToken !== fetchTokenRef.current) return;
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar os usuários');
        setCurrentData([]);
        setAmount(0);
      })
      .finally(() => {
        if (myToken === fetchTokenRef.current) setLoading(false);
      });
  }, [
    currentPage,
    itensPerPage,
    currentEmpresa,
    debouncedFilterText,
    currentRole,
    currentStatus,
  ]);

  useEffect(() => {
    buscaListaEmpresasFiltro();
  }, [buscaListaEmpresasFiltro]);

  useEffect(() => {
    buscaUsuarios();
    // Cleanup: bump token pra ignorar response em-voo quando o componente
    // desmontar (evita setState em componente desmontado).
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [buscaUsuarios, telaConcluido]);

  // Reseta pra pagina 1 quando filtros mudam.
  useEffect(() => {
    setCurrentPage(0);
  }, [
    itensPerPage,
    currentRole,
    currentStatus,
    currentEmpresa,
    debouncedFilterText,
  ]);

  // Preserva o tipo porque company e customer podem ter o mesmo id e devem
  // ser persistidos em colunas diferentes.
  const listaDeEmpresasFormatadaParaSelect = useMemo(
    () =>
      listaDeEmpresasRelacaoUsuario?.length
        ? listaDeEmpresasRelacaoUsuario.map(({ id, name: label, type }) => ({
            label,
            value: `${id}-${type}`,
          }))
        : [],
    [listaDeEmpresasRelacaoUsuario]
  );

  // Empresas com value "id-type" pra select de filtro (evita colisao
  // entre customer e company com mesmo id).
  const empresasFiltroOptions = useMemo(
    () =>
      listaDeEmpresasRelacaoUsuario?.length
        ? listaDeEmpresasRelacaoUsuario.map(({ id, type, name }) => ({
            label: name,
            value: `${id}-${type}`,
          }))
        : [],
    [listaDeEmpresasRelacaoUsuario]
  );

  // ============================================================
  // Acoes — cadastrar / editar role / toggle ativacao / delete.
  // ============================================================
  const cadastrarUsuarioNovo = (values) => {
    const targetId = Number(values.empresa);
    const body = {
      name: values.name.trim(),
      role: values.papel,
      email: values.email.trim().toLowerCase(),
      status: true,
    };

    if (values.company) {
      body.company = user.company.id;
    } else if (!Number.isInteger(targetId) || targetId <= 0) {
      notifyError('Selecione uma empresa válida');
      return;
    } else if (values.empresaType === 'company') {
      body.company = targetId;
    } else {
      body.customer = targetId;
    }

    setCreatingUser(true);
    postNewUser(
      user.customer ? `customer=${user.customer.id}` : '',
      user.company ? `company=${user.company.id}` : '',
      body
    )
      .then(() => {
        setIsModalVisible(false);
        setTelaConcluido(false);
        notifySuccess('Usuário cadastrado com sucesso');
        buscaUsuarios();
      })
      .catch((error) => {
        notifyError(error, 'Ocorreu um erro ao salvar o usuário');
      })
      .finally(() => setCreatingUser(false));
  };

  // Editar role inline via row-select — optimistic: atualiza state
  // local imediato e reverte se a request falhar. Evita reload da lista.
  const editarUsuarioRole = (row, novoRole) => {
    const prevRole = row.role;
    setCurrentData((rows) =>
      rows.map((u) => (u.id === row.id ? { ...u, role: novoRole } : u))
    );
    postEditUser(row.id, {
      name: row.name || '',
      email: row.email || '',
      role: novoRole,
    })
      .then(() => {
        notifySuccess('Usuário foi editado com sucesso');
      })
      .catch((error) => {
        // Reverte
        setCurrentData((rows) =>
          rows.map((u) => (u.id === row.id ? { ...u, role: prevRole } : u))
        );
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar o usuário');
      });
  };

  // Toggle de status (ativo/inativo). Optimistic: muda state local
  // imediato e reverte se falhar. Evita reload da lista inteira.
  // Bloqueio: nao pode alterar o proprio usuario nem quando status
  // veio como null (sem state).
  const toggleAtivo = async (row) => {
    if (user.id === row.id) {
      notifyError('Não é possível editar seu próprio usuário.');
      return;
    }
    if (row.status === null) return;
    const next = !row.status;
    // Optimistic update
    setCurrentData((rows) =>
      rows.map((u) => (u.id === row.id ? { ...u, status: next } : u))
    );
    try {
      await postEditStatusUser(row.id, next);
    } catch (error) {
      // Reverte
      setCurrentData((rows) =>
        rows.map((u) => (u.id === row.id ? { ...u, status: !next } : u))
      );
      if (error && error.message) notifyError(error.message);
      else notifyError('Ocorreu um erro ao alterar o status');
    }
  };

  const openEdit = (row) => {
    if (user.id === row.id) {
      notifyError('Não é possível editar seu próprio usuário.');
      return;
    }
    setUsuarioSelecionado({
      id: row.id || '',
      name: row.name || '',
      email: row.email || '',
      empresa: row.customer
        ? `${row.customer.id}-customer`
        : row.company
        ? `${row.company.id}-company`
        : '',
      company:
        !!row.company && !!user.company && row.company.id === user.company.id,
      papel: row.role || '',
      status: row.status,
    });
    setIsModalEditVisible(true);
  };

  const editarUsuario = (values) => {
    const targetId = Number(values.empresa);
    const body = {
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      role: values.papel,
    };

    if (values.company) {
      body.company = user.company.id;
    } else if (!Number.isInteger(targetId) || targetId <= 0) {
      notifyError('Selecione uma empresa válida');
      setConfirmationModal(false);
      return;
    } else if (values.empresaType === 'company') {
      body.company = targetId;
    } else {
      body.customer = targetId;
    }

    postEditUser(values.id, body)
      .then(async () => {
        // Nova senha (opcional): so quando o admin preencheu no form.
        // Feito apos o update dos dados; erro aqui e reportado sem
        // reverter o update ja concluido.
        if (values.password) {
          try {
            await setUserPassword(values.id, values.password);
            notifySuccess('Senha atualizada com sucesso');
          } catch (err) {
            notifyError(
              (err && err.message) || 'Nao foi possivel atualizar a senha',
            );
          }
        }
        setIsModalEditVisible(false);
        setConfirmationModal(false);
        setIsModalEditVisibleConcluido(true);
        buscaUsuarios();
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar o usuário');
        setConfirmationModal(false);
      });
  };

  const deleteUser = async (row) => {
    setIsDeletingUser(row.id);
    try {
      await deleteExistentUser(row.id);
      notifySuccess('Usuário foi deletado com sucesso');
      buscaUsuarios();
    } catch (error) {
      if (error && error.message) notifyError(error.message);
      else notifyError('Ocorreu um erro ao deletar o usuário');
    } finally {
      setIsDeletingUser('');
      setUserToDelete(null);
    }
  };

  // Quem tem direito a ver/usar a coluna de exclusao — so o role
  // "higra" da empresa Higra (idem regra antiga em TableUsuarios).
  const canDelete =
    user.role === 'higra' && user.company && user.company.name === 'Higra';

  // ============================================================
  // Paginacao — lista de paginas com elipses (max 7 visiveis).
  // ============================================================
  const pageCount = Math.ceil(amount / itensPerPage) || 0;
  const pagList = useMemo(() => {
    const list = [];
    const total = pageCount;
    const pg = currentPage + 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('…');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('…');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [pageCount, currentPage]);

  return (
    <s.Container className="fade-in">
      {/* Page Header (.ph) — titulo + sub + contador "<b>X</b> usuarios" */}
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Usuários</s.PageTitle>
          <s.PageSub>
            Gerencie permissões, empresas vinculadas e ativações.
          </s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{amount}</b> {amount === 1 ? 'usuário' : 'usuários'}
        </s.PageCount>
      </s.PageHeader>

      {/* Filters (.filters) — 5-col grid: busca | cliente | acesso | status | CTA */}
      <s.Filters>
        <s.Search>
          {ISearch}
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Pesquise por nome, e-mail ou empresa"
          />
        </s.Search>

        <s.SelCell>
          <select
            value={currentEmpresa}
            onChange={(e) => setCurrentEmpresa(e.target.value)}
            disabled={!user.company}
          >
            <option value="">Cliente</option>
            {empresasFiltroOptions
              .slice()
              .sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', 'pt-BR', {
                  sensitivity: 'base',
                })
              )
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentRole}
            onChange={(e) => setCurrentRole(e.target.value)}
          >
            <option value="">Acesso</option>
            <option value="admin">Admin Local</option>
            <option value="operador">Operador</option>
            <option value="visualizador">Visualizador</option>
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentStatus}
            onChange={(e) => setCurrentStatus(e.target.value)}
          >
            <option value="">Status</option>
            <option value="true">Ativo</option>
            <option value="false">Inativo</option>
          </select>
        </s.SelCell>

        <s.Cta type="button" onClick={() => setIsModalVisible(true)}>
          <span className="ic">{IPlus}</span>
          <span className="tx">
            <span className="t">Cadastro</span>
            <span className="s">Inserir usuário</span>
          </span>
        </s.Cta>
      </s.Filters>

      {/* Table — 9 col grid:
          Editar | Usuario | Empresa | Email | Acesso | Ultimo acesso | Status | Ativacao | Excluir */}
      <s.TableWrap>
        <s.Table>
          <s.Th>Editar</s.Th>
          <s.Th>Usuário</s.Th>
          <s.Th>Empresa</s.Th>
          <s.Th>Email de Contato</s.Th>
          <s.Th>Acesso</s.Th>
          <s.Th>Último acesso</s.Th>
          <s.Th>Status</s.Th>
          <s.Th>Ativação</s.Th>
          <s.Th>Excluir</s.Th>

          {loading ? (
            Array.from(
              { length: itensPerPage },
              (_, i) => `sk-${itensPerPage}-${i}`
            ).map((key) => (
              <s.Tr key={key}>
                <s.SkeletonCell $w="40%" />
                <s.SkeletonCell $w="70%" />
                <s.SkeletonCell $w="60%" />
                <s.SkeletonCell $w="80%" />
                <s.SkeletonCell $w="65%" />
                <s.SkeletonCell $w="70%" />
                <s.SkeletonCell $w="50%" />
                <s.SkeletonCell $w="40%" />
                <s.SkeletonCell $w="40%" />
              </s.Tr>
            ))
          ) : currentData.length ? (
            currentData.map((row) => {
              const empresaNome = row.customer
                ? row.customer.name
                : row.company
                ? row.company.name
                : '—';
              // Bloqueia mudanca de role: admin sem company, role higra,
              // ou o proprio usuario logado.
              const roleDisabled =
                (row.role === 'admin' && !user.company) ||
                row.role === 'higra' ||
                user.id === row.id;
              // Bloqueia toggle: nao pode editar a si mesmo + status null.
              const toggleDisabled = row.status === null || user.id === row.id;
              const lastAccessLabel = formatLastAccess(row.lastAccess);

              return (
                <s.Tr key={row.id}>
                  <s.Tc data-label="Editar">
                    <s.IconBtn
                      type="button"
                      title="Editar"
                      aria-label={`Editar usuário ${row.name}`}
                      onClick={() => openEdit(row)}
                    >
                      <Icon name="pencil" size={16} />
                    </s.IconBtn>
                  </s.Tc>
                  <s.TcNome>{row.name}</s.TcNome>
                  <s.TcEmpresa>{empresaNome}</s.TcEmpresa>
                  <s.TcEmail>{row.email}</s.TcEmail>

                  <s.Tc data-label="Acesso">
                    <s.RowSelect
                      value={row.role || ''}
                      disabled={roleDisabled}
                      onChange={(e) => editarUsuarioRole(row, e.target.value)}
                    >
                      {row.role &&
                      !listPapel.find((o) => o.value === row.role) ? (
                        // Role atual nao esta na lista filtrada (ex: higra) —
                        // mantem como option pra nao "sumir" o valor.
                        <option value={row.role}>
                          {ROLE_LABEL[row.role] || row.role}
                        </option>
                      ) : null}
                      <option value="">Definir</option>
                      {listPapel.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </s.RowSelect>
                  </s.Tc>

                  <s.TcLastAccess title={lastAccessLabel}>
                    {lastAccessLabel}
                  </s.TcLastAccess>

                  <s.Tc data-label="Status">
                    <s.StatusDot $on={!!row.status}>
                      {row.status ? 'Ativo' : 'Inativo'}
                    </s.StatusDot>
                  </s.Tc>

                  <s.Tc data-label="Ativação">
                    <s.Toggle
                      type="button"
                      $on={!!row.status}
                      disabled={toggleDisabled}
                      onClick={() => toggleAtivo(row)}
                      title={row.status ? 'Desativar' : 'Ativar'}
                    />
                  </s.Tc>

                  <s.Tc data-label="Excluir">
                    {canDelete ? (
                      <s.IconBtn
                        type="button"
                        className="danger"
                        title="Excluir"
                        aria-label={`Excluir usuário ${row.name}`}
                        disabled={isDeletingUser === row.id}
                        onClick={() => setUserToDelete(row)}
                      >
                        <Icon name="trash-2" size={16} />
                      </s.IconBtn>
                    ) : (
                      <s.IconBtn
                        type="button"
                        disabled
                        title="Sem permissão"
                        aria-label="Excluir usuário (sem permissão)"
                      >
                        <Icon name="trash-2" size={16} />
                      </s.IconBtn>
                    )}
                  </s.Tc>
                </s.Tr>
              );
            })
          ) : (
            <s.EmptyRow>
              <EmptyState
                title="Nenhum usuário encontrado"
                description="Ajuste a busca ou os filtros e tente novamente."
                actionLabel="Limpar filtros"
                onAction={() => {
                  setFilterText('');
                  setCurrentEmpresa('');
                  setCurrentRole('');
                  setCurrentStatus('');
                }}
              />
            </s.EmptyRow>
          )}
        </s.Table>
      </s.TableWrap>

      {/* Pager — Anterior / 1 2 3 … / Proximo + exibir N */}
      <s.Pager>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0 || pageCount === 0}
        >
          Anterior
        </button>
        <div className="pages">
          {pagList.map((p, idx) =>
            p === '…' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`dots-${idx}`} className="dots">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                onClick={() => setCurrentPage(p - 1)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
          disabled={currentPage >= pageCount - 1 || pageCount === 0}
        >
          Próximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itensPerPage}
            onChange={(e) => setItensPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>

      {/* Cadastro de novo usuario */}
      {isModalVisible ? (
        <Suspense fallback={null}>
          <CadastroUsuarioModal
            onClose={() => {
              setIsModalVisible(false);
              setTelaConcluido(false);
              buscaUsuarios();
            }}
            onCloseConfirmation={setCloseConfirmation}
            submit={cadastrarUsuarioNovo}
            telaConcluido={telaConcluido}
            setTelaConcluido={setTelaConcluido}
            setConfirmationModal={() => {}}
            currentCustomers={listaDeEmpresasFormatadaParaSelect}
            formikInitialValues={formikInitialValues}
            title="Cadastro de Usuário"
            dadosAbaixo="Preencha os dados abaixo:"
            msgButton="Será enviado um convite para o email cadastrado."
            labelButton="CADASTRAR"
            sucesso="Usuário cadastrado com sucesso!"
            iconFinal={icons.AcessosIconFinish}
            disabled={creatingUser}
          />
        </Suspense>
      ) : null}

      {/* Edicao de usuario (modal cadastro reaproveitado) */}
      {confirmationModal ? (
        <ModalConfirmation
          onClose={() => editarUsuario(usuarioEditado)}
          onCloseConfirmation={setConfirmationModal}
        />
      ) : null}
      {isModalEditVisibleConcluido ? (
        <Suspense fallback={null}>
          <CadastroUsuarioModal
            onClose={() => setIsModalEditVisibleConcluido(false)}
            onCloseConfirmation={setCloseConfirmation}
            setTelaConcluido={setIsModalEditVisible}
            formikInitialValues={usuarioSelecionado}
            currentCustomers={listaDeEmpresasFormatadaParaSelect}
            telaConcluido={isModalEditVisibleConcluido}
            title="Editar Usuario"
            sucesso="Usuário editado com sucesso!"
            iconFinal={icons.AcessosIconEdit}
          />
        </Suspense>
      ) : null}
      {isModalEditVisible ? (
        <Suspense fallback={null}>
          <CadastroUsuarioModal
            onClose={() => {
              setIsModalEditVisible(false);
              buscaUsuarios();
            }}
            submit={setUsuarioEditado}
            onCloseConfirmation={setCloseConfirmation}
            setConfirmationModal={setConfirmationModal}
            setTelaConcluido={setIsModalEditVisible}
            telaConcluido={isModalEditVisibleConcluido}
            formikInitialValues={usuarioSelecionado}
            currentCustomers={listaDeEmpresasFormatadaParaSelect}
            title="Editar Usuário"
            dadosAbaixo="Modifique os dados abaixo:"
            msgButton="Confirme suas modificações"
            labelButton="SALVAR"
            showPasswordFields
            sucesso="Usuário editado com sucesso!"
            iconFinal={icons.AcessosIconEdit}
          />
        </Suspense>
      ) : null}
      {closeConfirmation ? (
        <ModalClose
          onClose={() => {
            setIsModalVisible(false);
            setIsModalEditVisible(false);
            setTelaConcluido(false);
            setCloseConfirmation(false);
            buscaUsuarios();
          }}
          onCloseConfirmation={setCloseConfirmation}
        />
      ) : null}

      {/* Confirmacao de exclusao */}
      {userToDelete ? (
        <ModalConfirmation
          labelTitle={`Excluir o usuário "${userToDelete.name}"? Esta ação não pode ser desfeita.`}
          labelConfirmation="EXCLUIR"
          labelClose="CANCELAR"
          onCloseConfirmation={() => setUserToDelete(null)}
          onClose={() => deleteUser(userToDelete)}
        />
      ) : null}
    </s.Container>
  );
};

export default RelacaoUsuarios;
